import type { ChatMessage, ChatUser, Conversation, MessageAttachment } from '../shared/types.ts';
import { UserAvatar } from '@ah/design';
import { Paperclip } from 'lucide-react';

export function displayName(u: ChatUser): string {
  if (u.givenName || u.familyName) return [u.givenName, u.familyName].filter(Boolean).join(' ');
  return u.name ?? 'Unknown';
}

export function convTitle(conv: Conversation, currentUserId: string): string {
  if (conv.name) return conv.name;
  const others = conv.participants.filter((p) => p.user.id !== currentUserId);
  if (others.length === 0) return 'Just you';
  return others.map((p) => displayName(p.user)).join(', ');
}

export function convAvatar(conv: Conversation, currentUserId: string): ChatUser | null {
  const others = conv.participants.filter((p) => p.user.id !== currentUserId);
  return others[0]?.user ?? null;
}

export function timeHHMM(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function timeAgoShort(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDayDivider(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
}

export function sameDay(a: string, b: string): boolean {
  return new Date(a).toDateString() === new Date(b).toDateString();
}

export function avatarProps(u: ChatUser) {
  return { src: u.picture ?? undefined, name: displayName(u) };
}

// ── Bubble ─────────────────────────────────────────────────────────────

export function MessageBubble({
  msg,
  mine,
  showAuthor,
  isGroup = false,
}: {
  msg: ChatMessage;
  mine: boolean;
  showAuthor: boolean;
  isGroup?: boolean;
}) {
  const imgs = msg.attachments.filter((a) => a.mimeType.startsWith('image/'));
  const files = msg.attachments.filter((a) => !a.mimeType.startsWith('image/'));
  const showAvatarSlot = isGroup && !mine;

  return (
    <div className={`flex items-end gap-2 ${mine ? 'justify-end' : 'justify-start'}`}>
      {showAvatarSlot && (
        showAuthor ? (
          <UserAvatar {...avatarProps(msg.author)} className="size-7 shrink-0 mb-0.5" />
        ) : (
          <div className="size-7 shrink-0" aria-hidden />
        )
      )}
      <div
        className={
          'max-w-[75%] min-w-0 rounded-2xl px-4 py-2.5 text-[15px] leading-snug [overflow-wrap:anywhere] ' +
          (mine
            ? 'bg-primary text-primary-foreground rounded-br-sm'
            : 'bg-muted text-foreground rounded-bl-sm')
        }
      >
        {showAuthor && !mine && (
          <div className="text-xs font-semibold opacity-70 mb-0.5">
            {displayName(msg.author)}
          </div>
        )}

        {imgs.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {imgs.map((a) => (
              <a key={a.id} href={a.url} target="_blank" rel="noopener noreferrer">
                <img
                  src={a.url}
                  alt={a.originalName}
                  className="rounded-lg max-w-[200px] max-h-[200px] object-cover"
                />
              </a>
            ))}
          </div>
        )}

        {files.length > 0 && (
          <div className="flex flex-col gap-1 mb-1">
            {files.map((a) => (
              <AttachmentChip key={a.id} att={a} mine={mine} />
            ))}
          </div>
        )}

        {msg.content && <div>{msg.content}</div>}

        {msg.linkUrl && msg.linkTitle && (
          <a
            href={msg.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`mt-1 block rounded-lg overflow-hidden ${mine ? 'bg-primary-foreground/10' : 'bg-background/70'}`}
          >
            {msg.linkImageUrl && (
              <img src={msg.linkImageUrl} alt="" className="w-full h-24 object-cover" />
            )}
            <div className="p-2">
              {msg.linkDomain && (
                <div className="text-[10px] uppercase tracking-wide opacity-70">
                  {msg.linkDomain}
                </div>
              )}
              <div className="text-xs font-medium line-clamp-2">{msg.linkTitle}</div>
            </div>
          </a>
        )}

        <div className="text-xs opacity-60 mt-1">{timeHHMM(msg.createdAt)}</div>
      </div>
    </div>
  );
}

export function AttachmentChip({ att, mine }: { att: MessageAttachment; mine: boolean }) {
  return (
    <a
      href={att.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${mine ? 'bg-primary-foreground/15' : 'bg-background/70'}`}
    >
      <Paperclip className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate flex-1">{att.originalName}</span>
      <span className="opacity-60 shrink-0">{formatFileSize(att.fileSize)}</span>
    </a>
  );
}

// ── Skeletons ──────────────────────────────────────────────────────────

export function ConversationsSkeleton() {
  return (
    <ul className="animate-pulse">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="flex items-start gap-3 px-4 py-3 border-b border-border">
          <div className="size-11 rounded-full bg-muted shrink-0" />
          <div className="flex-1 space-y-2 pt-1">
            <div className="h-4 w-1/2 rounded bg-muted" />
            <div className="h-3 w-3/4 rounded bg-muted" />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function MessagesSkeleton() {
  // Widths and heights roughly mimic a natural back-and-forth: short replies,
  // a longer paragraph, a one-liner, etc. Heights vary to suggest multi-line
  // bubbles. Grouped pairs (two bubbles from same side) with a small gap.
  const rows: { mine: boolean; w: string; h: string; tight?: boolean }[] = [
    { mine: false, w: 'w-56', h: 'h-9' },
    { mine: false, w: 'w-40', h: 'h-8', tight: true },
    { mine: true, w: 'w-32', h: 'h-8' },
    { mine: false, w: 'w-64', h: 'h-14' },
    { mine: true, w: 'w-48', h: 'h-8' },
    { mine: true, w: 'w-24', h: 'h-8', tight: true },
    { mine: false, w: 'w-44', h: 'h-8' },
  ];
  return (
    <div className="animate-pulse flex flex-col gap-2 px-4 py-3">
      {rows.map((r, i) => (
        <div
          key={i}
          className={`flex ${r.mine ? 'justify-end' : 'justify-start'} ${r.tight ? '-mt-1' : ''}`}
        >
          <div
            className={`${r.h} ${r.w} max-w-[75%] rounded-2xl bg-muted ${r.mine ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
          />
        </div>
      ))}
    </div>
  );
}

export function ConvRowAvatar({
  conv,
  currentUserId,
}: {
  conv: Conversation;
  currentUserId: string;
}) {
  const avatar = convAvatar(conv, currentUserId);
  if (conv.isGroup && !avatar) {
    return (
      <div className="size-11 rounded-full bg-muted flex items-center justify-center shrink-0">
        <span className="text-sm font-semibold text-muted-foreground">
          {(conv.name ?? 'G').charAt(0).toUpperCase()}
        </span>
      </div>
    );
  }
  if (avatar) {
    return <UserAvatar {...avatarProps(avatar)} className="size-11 shrink-0" />;
  }
  return <div className="size-11 rounded-full bg-muted shrink-0" />;
}
